import {
  RichTextField,
  Field,
  ImageField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import { GraphQLRequestClient } from "@sitecore-jss/sitecore-jss-nextjs/graphql";

export interface GetLevel2DisclaimersRequest {
  language: string; // The language of the item to query.
  stateCode: string; // The state code of the item to query.
  rootId: string; // The root ID of the item to query.
}

export interface GetLevel2DisclaimersResponse {
  search?: {
    total: number;
    results: {
      contactHeader?: Field<string>;
      contactText?: RichTextField;
      feesHeader?: Field<string>;
      feesDisclaimer?: RichTextField;
      floorplansHeader?: Field<string>;
      floorplansDisclaimer?: RichTextField;
    }[];
  };
}

export interface GetLevel2PricingAndCostsRequest {
  language: string; // The language of the item to query.
  stateCode: string; // The state code of the item to query.
  rootId: string; // The root ID of the item to query.
}

export interface GetLevel2PricingAndCostsResponse {
  search?: {
    total: number;
    results: {
      pricingAndCostsHeader?: Field<string>;
      pricingAndCostsText?: RichTextField;
    }[];
  };
}

export interface GetLevelsRequest {
  language: string; // The language of the item to query.
  stateCode: string; // The state code of the item to query.
  marketAreaName: string; // The market area name of the item to query.
  neighborhoodName: string; // The neighborhood name of the item to query.
  rootId: string; // The root ID of the item to query.
}

export interface GetLevelsResponse {
  search?: {
    total: number;
    results: {
      pricingAndCostsHeader?: Field<string>;
      pricingAndCostsText?: RichTextField;
      contactHeader?: Field<string>;
      contactText?: RichTextField;
      feesHeader?: Field<string>;
      feesDisclaimer?: RichTextField;
      floorplansHeader?: Field<string>;
      floorplansDisclaimer?: RichTextField;
      name?: string;
      marketAreaName?: Field<string>;
      neighborhoodName?: Field<string>;
      path?: string;
      url?: {
        path?: string;
      };
      metaDescription?: Field<string>;
      tileImageAEM?: ImageField;
      headerImageAEM?: ImageField;
    }[];
  };
}

/**
 * Fetches the contact and disclaimers text at a state level based on the provided request.
 *
 * @param {GraphQLRequestClient} client - The GraphQL client to use for the request.
 * @param {GetLevel2DisclaimersRequest} request - The request containing the context language, stateCode and rootId.
 * @returns {Promise<GetLevel2DisclaimersResponse | null>} A promise that resolves to the level 2 disclaimers response or null if the request fails.
 */
export const getLevel2Disclaimers = async (
  client: GraphQLRequestClient,
  request: GetLevel2DisclaimersRequest
): Promise<GetLevel2DisclaimersResponse | null> => {
  return await client
    .request<GetLevel2DisclaimersResponse>(
      GetLevel2DisclaimersQuery.replace("[lang]", request.language)
        .replace("[stateCode]", request.stateCode)
        .replace("[rootId]", request.rootId)
    )
    .catch((error) => {
      console.error(`Failed to get level 2 disclaimer data`, error);
      return null;
    });
};

/**
 * Fetches the pricing and costs text at a state level based on the provided request.
 *
 * @param {GraphQLRequestClient} client - The GraphQL client to use for the request.
 * @param {GetLevel2PricingAndCostsRequest} request - The request containing the context language, stateCode and rootId.
 * @returns {Promise<GetLevel2PricingAndCostsResponse | null>} A promise that resolves to the level 2 pricing and costs response or null if the request fails.
 */
export const getLevel2PricingAndCosts = async (
  client: GraphQLRequestClient,
  request: GetLevel2PricingAndCostsRequest
): Promise<GetLevel2PricingAndCostsResponse | null> => {
  return await client
    .request<GetLevel2PricingAndCostsResponse>(
      GetLevel2PricingAndCostsQuery.replace("[lang]", request.language)
        .replace("[stateCode]", request.stateCode)
        .replace("[rootId]", request.rootId)
    )
    .catch((error) => {
      console.error(`Failed to get level 2 pricing and costs data`, error);
      return null;
    });
};

/**
 * Fetches the pricing and costs text at a state level based on the provided request.
 *
 * @param {GraphQLRequestClient} client - The GraphQL client to use for the request.
 * @param {GetLevelsRequest} request - The request containing the context language, rootId and locations to match.
 * @returns {Promise<GetLevelsResponse | null>} A promise that resolves to the locations data or null if the request fails.
 */
export const getLevels = async (
  client: GraphQLRequestClient,
  request: GetLevelsRequest
): Promise<GetLevelsResponse | null> => {
  const query = GetLevelsQuery.replace("[lang]", request.language)
    .replace("[stateCode]", request.stateCode)
    .replace("[marketAreaName]", request.marketAreaName)
    .replace("[neighborhoodName]", request.neighborhoodName)
    .replace("[rootId]", request.rootId);

  return await client.request<GetLevelsResponse>(query).catch((error) => {
    console.error(`Failed to get levels data`, error);
    return null;
  });
};

/**
 * The GraphQL query to fetch the contact and disclaimer text for a state.
 */
const GetLevel2DisclaimersQuery = /* GraphQL */ `
  query Search {
    search(
      where: {
        AND: [
          { name: "_path", value: "[rootId]", operator: CONTAINS }
          { name: "_hasLayout", value: "true" }
          { name: "_language", value: "[lang]" }
          { name: "_name", value: "[stateCode]" }
          {
            name: "_templates"
            value: "{885627E2-6A81-4EE3-B2ED-E46E01FF93D4}"
          }
        ]
      }
    ) {
      total
      results {
        ... on PropertyDetailsContactDisclaimers {
          contactHeader {
            value
          }
          contactText {
            value
          }
          feesHeader {
            value
          }
          feesDisclaimer {
            value
          }
          floorplansHeader {
            value
          }
          floorplansDisclaimer {
            value
          }
        }
      }
    }
  }
`;

/**
 * The GraphQL query to fetch the pricing and costs text for a state.
 */
const GetLevel2PricingAndCostsQuery = /* GraphQL */ `
  query Search {
    search(
      where: {
        AND: [
          { name: "_path", value: "[rootId]", operator: CONTAINS }
          { name: "_hasLayout", value: "true" }
          { name: "_language", value: "[lang]" }
          { name: "_name", value: "[stateCode]" }
          {
            name: "_templates"
            value: "{885627E2-6A81-4EE3-B2ED-E46E01FF93D4}"
          }
        ]
      }
    ) {
      total
      results {
        ... on _PricingAndCosts {
          pricingAndCostsHeader {
            value
          }
          pricingAndCostsText {
            value
          }
        }
      }
    }
  }
`;

/**
 * The GraphQL query to fetch state, market area and neighborhood info in one go.
 */
const GetLevelsQuery = /* GraphQL */ `
  query Search {
    search(
      where: {
        AND: [
          { name: "_path", value: "[rootId]", operator: CONTAINS }
          { name: "_hasLayout", value: "true" }
          { name: "_language", value: "[lang]" }
          {
            OR: [
              { name: "_name", value: "[stateCode]" }
              { name: "name", value: "[marketAreaName]" }
              { name: "name", value: "[neighborhoodName]" }
            ]
          }
        ]
      }
    ) {
      total
      results {
        ... on LocationGuideLevel2Page {
          pricingAndCostsHeader {
            value
          }
          pricingAndCostsText {
            value
          }
          contactHeader {
            value
          }
          contactText {
            value
          }
          feesHeader {
            value
          }
          feesDisclaimer {
            value
          }
          floorplansHeader {
            value
          }
          floorplansDisclaimer {
            value
          }
          name
          path
          url {
            path
          }
        }
        ... on LocationGuideLevel3Page {
          metaDescription {
            value
          }
          tileImageAEM {
            value
          }
          headerImageAEM {
            value
          }
          marketAreaName: field(name: "name") {
            value
          }
          path
          url {
            path
          }
        }
        ... on LocationGuideLevel4Page {
          metaDescription {
            value
          }
          tileImageAEM {
            value
          }
          headerImageAEM {
            value
          }
          neighborhoodName: field(name: "name") {
            value
          }
          path
          url {
            path
          }
        }
      }
    }
  }
`;
